import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Grid,
    CircularProgress,
    Autocomplete,
    TextField
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import classes from "../Assets/Styles/Global.module.css";
import { getAvailableProjects } from "../features/projects/api";

// `fullWidth` drops the 420px cap so the field fills a narrow container.
const ProjectSearch = ({
    onProjectSelect,
    excludeProjectId,
    showTitle = true,
    fullWidth = false,
}) => {
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [open, setOpen] = useState(false);

    // Separate API call function
    const fetchProjectsAPI = useCallback(async (searchTerm) => {
        setLoading(true);
        try {
            const response = await getAvailableProjects({
                projectName: searchTerm || "",
                limit: 20 // Limit the number of results
            });

            const projects = Array.isArray(response.data) ? response.data : [];
            setOptions(projects.filter((project) => project.id !== excludeProjectId));
        } catch (error) {
            console.error("Error searching projects:", error);
            toast.error("Failed to search projects");
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [excludeProjectId]);

    // Debounced search function
    const debouncedSearch = useMemo(
        () => debounce((term) => {
            if (term.length >= 2) {
                fetchProjectsAPI(term);
            }
        }, 500),
        [fetchProjectsAPI]
    );

    // Cleanup effect
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    // Instead of loading all projects on open, show a placeholder message
    useEffect(() => {
        if (open && options.length === 0 && inputValue.length < 2) {
            setOptions([{ id: "placeholder", name: "Type at least 2 characters to search..." }]);
        }
    }, [open, options.length, inputValue.length]);

    const handleInputChange = (event, newInputValue, reason) => {
        if (reason === "reset") {
            return;
        }

        setInputValue(newInputValue);
        debouncedSearch(newInputValue);

        // Clear placeholder when typing
        if (newInputValue.length < 2 && options.length > 0 && options[0]?.id !== "placeholder") {
            setOptions([{ id: "placeholder", name: "Type at least 2 characters to search..." }]);
        }
    };

    return (
        <Grid container spacing={2} sx={{ marginBottom: showTitle ? 4 : 0 }}>
            {showTitle && (
                <Grid item xs={12}>
                    <Grid item className={classes["midHeader"]} marginBottom={1.5}>
                        Import Cast
                    </Grid>
                </Grid>
            )}

            <Grid item sx={{ width: "100%", maxWidth: fullWidth ? "none" : 420 }}>
                <Autocomplete
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    options={options}
                    getOptionLabel={(option) => option?.name || ""}
                    filterOptions={(x) => x}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    // Prevent selection of placeholder
                    isOptionEqualToValue={(option, value) =>
                        option.id === value.id && option.id !== "placeholder"
                    }
                    loading={loading}
                    value={selectedProject}
                    onChange={(event, newValue) => {
                        // Don't select placeholder
                        if (newValue && newValue.id !== 'placeholder') {
                            setSelectedProject(newValue);
                            if (onProjectSelect) {
                                onProjectSelect(newValue);
                            }
                        }
                    }}
                    onInputChange={handleInputChange}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Search existing projects"
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading ? (
                                            <CircularProgress color="inherit" size={20} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                        />
                    )}
                    renderOption={(props, option) => {
                        // If this is a placeholder option, render it differently
                        if (option.id === "placeholder") {
                            return (
                                <li {...props} style={{ fontStyle: "italic", color: "#666" }}>
                                    {option.name}
                                </li>
                            );
                        }

                        const { key, ...otherProps } = props;
                        return (
                            <li key={option.id} {...otherProps}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    padding: '4px 0'
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 500 }}>{option.name}</div>
                                        <div style={{ fontSize: 'var(--font-size-normal)', color: '#666' }}>
                                            {option.charactersCount || 0} characters
                                        </div>
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 'var(--font-size-normal)',
                                            backgroundColor: '#e3f2fd',
                                            color: '#1976d2',
                                            padding: '2px 6px',
                                            borderRadius: "var(--radius-md)",
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(`/workpages/details/${option.id}`, '_blank', 'noopener,noreferrer');
                                        }}
                                    >
                                        <InfoIcon fontSize="small" />
                                        View
                                    </div>
                                </div>
                            </li>
                        );
                    }}
                    noOptionsText="No projects found"
                />
            </Grid>
        </Grid>
    );
};

export default ProjectSearch;